import React, { FC, useContext, useEffect, useState } from 'react';
import Section from '../../components/Main/Section/Section';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { GMBAuthContext } from '../../providers/GMBAuthProvider';
import CommunityManagementConfigurator from '../../components/ReviewMonitoringGuide/CommunityManagementConfigurator';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import UserService from '../../services/UserService';
import AlertConfigurator from '../../components/ReviewMonitoringGuide/AlertConfigurator';
import { AlertManagement } from '../../shared/interfaces/IAlerts';
import AlertService from '../../services/AlertService';
import { AuthContext } from '../../providers/AuthProvider';
import GMBService from '../../services/GMBService';

const defaultAlertSettings: AlertManagement = {
  active: false,
  id: 1,
  interval: 1,
  options: [
    {
      id: 1,
      name: 'DAILY',
    },
    {
      id: 2,
      name: 'WEEKLY',
    },
  ],
  type: 'NEW_CRAWL',
};

const Administration: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasRole } = useContext(AuthContext);
  const { setCurrentView } = useContext(CurrentViewContext);
  const { user, logout } = useContext(GMBAuthContext);
  const [gmbInterval, setGmbInterval] = useState<string>('24h');
  const [sleepMode, setSleepMode] = useState<boolean>(false);
  const [comLoading, setComLoading] = useState<boolean>(false);
  const [alertLoading, setAlertLoading] = useState<boolean>(false);
  const [newAlertSettings, setNewAlertSettings] = useState<AlertManagement>(defaultAlertSettings);
  const [updatedAlertSettings, setUpdatedAlertSettings] =
    useState<AlertManagement>(defaultAlertSettings);
  const [communityManagementSettings, setCommunityManagementSettings] =
    useState<CommunityManagement>({
      scoreThreshold: 5,
      languagePreference: 'de',
      salutationPrefix: '',
      useUsername: true,
      greeting: '',
      tonality: {
        preference: 0,
      },
      pronoun: {
        preference: 0,
      },
    });

  useEffect(() => {
    if (user) {
      setGmbInterval(user.interval);
    }
  }, [user, setGmbInterval]);

  useEffect(() => {
    setCurrentView(t('nav_administration'));
    document.title = `Reputativ ${t('nav_administration')}`;
  }, [setCurrentView, t]);

  const saveCommunityManagementSettings = async () => {
    if (!communityManagementSettings) return;
    await UserService.saveCommunityManagementSettings(communityManagementSettings, setComLoading);
  };

  const saveSleep = async () => {
    if (!communityManagementSettings) return;
    await UserService.saveUserSettings(
      { sleeping: sleepMode, communityManagement: communityManagementSettings },
      setComLoading
    );
  };

  const handleGmbIntervalUpdateSelectChange = async (ev: any) => {
    setGmbInterval(ev.target.value);
  };

  const saveGmbIntervalUpdate = async () => {
    await GMBService.setInterval(gmbInterval);
  };

  const saveAlertSettings = async () => {
    if (newAlertSettings && updatedAlertSettings) {
      await AlertService.saveAlertSettings(newAlertSettings, updatedAlertSettings, setAlertLoading);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const getCommunityManagementSettings = async () => {
      const response = await UserService.getCommunityManagementSettings(controller.signal);

      if (!response) return;
      if (!response.communityManagement) return;
      setCommunityManagementSettings(response.communityManagement);
      setSleepMode(response.sleeping);
    };

    (async () => await getCommunityManagementSettings())();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    updatedAlertSettings.id = 2;
    updatedAlertSettings.type = 'UPDATED_CRAWL';

    const fetchAlertSettings = async () => {
      const response = await AlertService.getAlertSettings(controller.signal);

      if (!response) return;

      if (response.alerts[0]) {
        setNewAlertSettings(response.alerts[0]);
      }

      if (response.alerts[1]) {
        setUpdatedAlertSettings(response.alerts[1]);
      }
    };

    fetchAlertSettings();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Section>
      {user && (
        <>
          <Typography variant="h5">{t('account')}</Typography>
          <Divider />
          <Box>
            <Typography
              variant="h6"
              sx={{ mt: theme.spacing(2) }}
            >
              Google
            </Typography>
            <Box sx={{ mt: theme.spacing(1) }}>
              {user.picture && (
                <img
                  src={user.picture}
                  width="64"
                  referrerPolicy="no-referrer"
                  alt="Google avatar"
                />
              )}
              {user.name && <Typography variant="body2">{user.name}</Typography>}
              {user.email && <Typography variant="body2">{user.email}</Typography>}
            </Box>
            <Box sx={{ mt: theme.spacing(1) }}>
              <Button
                variant="contained"
                onClick={async () => await logout()}
              >
                {t('logout')}
              </Button>
            </Box>
            {hasRole('can-set-gmb-interval') && (
              <Box sx={{ maxWidth: 450 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
                  <FormControl
                    size="small"
                    sx={{ mt: theme.spacing(1) }}
                  >
                    <InputLabel id="gmb-update-interval">{t('gmb_update_interval')}</InputLabel>
                    <Select
                      labelId="gmb-update-interval"
                      value={gmbInterval}
                      onChange={handleGmbIntervalUpdateSelectChange}
                      label={t('gmb_update_interval')}
                    >
                      <MenuItem value="12h">12h</MenuItem>
                      <MenuItem value="24h">24h</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Button
                  variant="contained"
                  sx={{ mt: theme.spacing(1) }}
                  onClick={async () => await saveGmbIntervalUpdate()}
                >
                  {t('save')}
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}

      {(hasRole('can-use-ai') || hasRole('can-use-alerts')) && (
        <Box sx={{ maxWidth: 450 }}>
          {hasRole('can-use-ai') && (
            <>
              <Typography
                variant="h5"
                sx={{ mt: user ? theme.spacing(4) : theme.spacing(0) }}
              >
                {t('community_management')}
              </Typography>
              <Divider />
              <CommunityManagementConfigurator
                settings={communityManagementSettings}
                onChange={setCommunityManagementSettings}
              />
              <Button
                disabled={comLoading}
                variant="contained"
                onClick={async () => await saveCommunityManagementSettings()}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('save')}
              </Button>
            </>
          )}

          {hasRole('can-use-alerts') && (
            <>
              <AlertConfigurator
                newAlert={newAlertSettings}
                updatedAlert={updatedAlertSettings}
                onChangeNew={setNewAlertSettings}
                onChangeUpdated={setUpdatedAlertSettings}
              />
              <Button
                disabled={alertLoading}
                variant="contained"
                onClick={async () => await saveAlertSettings()}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('save')}
              </Button>
            </>
          )}

          {hasRole('can-sleep') && (
            <Box sx={{ maxWidth: 450 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
                <Typography
                  variant="h5"
                  sx={{ mt: user ? theme.spacing(4) : theme.spacing(0) }}
                >
                  {t('sleep_mode')}
                </Typography>
                <Divider />
                <FormControl
                  size="small"
                  sx={{ mt: theme.spacing(1) }}
                >
                  <InputLabel id="sleep-mode">{t('sleep_mode')}</InputLabel>
                  <Select
                    labelId="sleep-mode"
                    value={sleepMode ? 'true' : 'false'}
                    onChange={(ev) => setSleepMode(ev.target.value == 'true')}
                    label={t('sleep_mode')}
                  >
                    <MenuItem value="false">{t('off')}</MenuItem>
                    <MenuItem value="true">{t('on')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button
                variant="contained"
                sx={{ mt: theme.spacing(1) }}
                onClick={async () => await saveSleep()}
              >
                {t('save')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Section>
  );
};

export default Administration;
